// eslint-disable-next-line import/no-unresolved -- leaflet.heat main is dist/leaflet-heat.js, resolved by bundler
import 'leaflet.heat';

import { useAppDispatch } from '_store';
import heatmapReducer from '_store/heatmap/heatmapReducers';
import { heatmapSelector } from '_store/heatmap/selectors';
import {
  aggregateWindow,
  getDataset,
  HeatmapCell,
  heatmapKey,
  HeatmapMetric,
  scaleAcrossSlots,
  valueRange,
} from '_store/heatmap/utils';
import { useLocalization } from '@fluent/react';
import { Button, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import L from 'leaflet';
import React, { FC, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

type Rgb = [number, number, number];

// Speed only (vehicle counts use leaflet.heat): green = slowest of the scale → red = fastest.
const SPEED_RAMP: Rgb[] = [
  [26, 152, 80],
  [254, 224, 139],
  [215, 48, 39],
];

const rampColor = (t: number): string => {
  const x = Math.max(0, Math.min(1, t)) * (SPEED_RAMP.length - 1);
  const i = Math.min(Math.floor(x), SPEED_RAMP.length - 2);
  const u = x - i;
  const rgb = SPEED_RAMP[i].map((v, k) => Math.round(v + (SPEED_RAMP[i + 1][k] - v) * u));
  return `rgb(${rgb[0]},${rgb[1]},${rgb[2]})`;
};

// Vehicle density: cold (few) → hot (many). Same stops feed leaflet.heat and the legend bar.
const HEAT_GRADIENT: Record<number, string> = {
  0.15: '#2c7bb6',
  0.35: '#00ccbc',
  0.55: '#a6d96a',
  0.75: '#fee08b',
  1: '#d7191c',
};

const legendCss = (metric: HeatmapMetric): string => {
  const stops =
    metric === 'counter'
      ? Object.entries(HEAT_GRADIENT) // integer key '1' is listed first by JS, so sort by position
          .sort(([a], [b]) => Number(a) - Number(b))
          .map(([at, c]) => `${c} ${Math.round(Number(at) * 100)}%`)
      : [0, 0.5, 1].map((t) => rampColor(t));
  return `linear-gradient(to top, ${stops.join(', ')})`;
};

const HEAT_RADIUS_M = 700; // blob radius on the ground, so heat grows with zoom like the cells it stands for
const HEAT_RADIUS_PX = [10, 60];

// Speed discs keep the same on-screen size at every zoom (the API gives the cell centre only, not its size).
const CELL_RADIUS_PX = 8;
const CANVAS_PAD = 0.2; // like L.Canvas: draw beyond the viewport so panning needs no redraw
const EARTH_CIRCUMFERENCE_M = 40075016.686;

const useStyles = makeStyles((theme) => ({
  overlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
  },
  loading: {
    backgroundColor: theme.palette.action.disabledBackground,
  },
  banner: {
    position: 'absolute',
    top: 12,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 4,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  legend: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 400,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: 84,
    padding: theme.spacing(0.5, 0.75),
    lineHeight: 1.2,
    borderRadius: 4,
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    textAlign: 'center',
  },
  legendBar: {
    width: 10,
    height: 72,
    margin: theme.spacing(0.25, 0),
    borderRadius: 2,
  },
}));

const HeatmapLayer: FC<{ map: L.Map | null }> = ({ map }) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { l10n } = useLocalization();
  const { active, baseRange, dateRange, scaleToVisibleData, granularityMs, status, error, dataKey } =
    useSelector(heatmapSelector);
  const [baseStart, baseEnd] = baseRange;
  const [slotStart, slotEnd] = dateRange;
  const metric: HeatmapMetric = active === 'speed_value' ? 'speed_value' : 'counter';
  const enabled = active !== 'inactive' && baseStart < baseEnd;
  const wholeRange = slotStart === baseStart && slotEnd === baseEnd;

  const request = () =>
    dispatch(heatmapReducer.actions.heatmapRequest({ metric, start: baseStart, end: baseEnd }));
  useEffect(() => {
    if (enabled) request();
  }, [enabled, metric, baseStart, baseEnd]);

  // Only trust the dataset when it is the one for the current request (not the previous metric's)
  const ds = enabled && dataKey === heatmapKey(metric, baseStart, baseEnd) ? getDataset(dataKey) : null;

  const cells = useMemo(
    (): HeatmapCell[] => (ds && slotStart < slotEnd ? aggregateWindow(ds, slotStart, slotEnd) : []),
    [ds, slotStart, slotEnd],
  );
  const range = useMemo(() => {
    if (!ds || slotStart >= slotEnd) return null;
    return scaleToVisibleData
      ? valueRange(cells)
      : scaleAcrossSlots(ds, baseStart, baseEnd, wholeRange ? baseEnd - baseStart : granularityMs);
  }, [ds, cells, scaleToVisibleData, baseStart, baseEnd, slotStart, slotEnd, wholeRange, granularityMs]);

  // Vehicle density: a real heat layer. max is fixed (or the slot's) so colours compare across slots.
  useEffect(() => {
    if (!map || metric !== 'counter' || cells.length === 0 || !range) return undefined;
    const radiusPx = () => {
      const metersPerPx = (EARTH_CIRCUMFERENCE_M * Math.cos((map.getCenter().lat * Math.PI) / 180)) /
        (256 * 2 ** map.getZoom());
      return Math.max(HEAT_RADIUS_PX[0], Math.min(HEAT_RADIUS_PX[1], HEAT_RADIUS_M / metersPerPx));
    };
    const LHeat = L as unknown as {
      heatLayer: (pts: Array<[number, number, number]>, o: Record<string, unknown>) => L.Layer & {
        setOptions: (o: Record<string, unknown>) => void;
      };
    };
    const layer = LHeat.heatLayer(
      cells.map((c) => [c.lat, c.lng, c.value] as [number, number, number]),
      {
        radius: radiusPx(),
        blur: 15,
        max: Math.max(range.max, 1),
        maxZoom: 1, // intensity is constant above zoom 1, so colours do not change while zooming
        minOpacity: 0.25,
        gradient: HEAT_GRADIENT,
      },
    );
    layer.addTo(map);
    const onZoom = () => layer.setOptions({ radius: radiusPx() });
    map.on('zoomend', onZoom);
    return () => {
      map.off('zoomend', onZoom);
      map.removeLayer(layer);
    };
  }, [map, cells, range, metric]);

  // Faint tint of the scale's lowest colour over the whole map (below the blobs), so "no vehicles"
  // reads as the cold end of the legend. ponytail: also tints cells the API omitted, which is the point.
  useEffect(() => {
    if (!map || metric !== 'counter' || cells.length === 0) return undefined;
    const paneName = 'heatmapTint';
    const pane = map.getPane(paneName) ?? map.createPane(paneName);
    pane.style.zIndex = '399'; // above tiles, below the heat canvas (overlayPane, 400)
    const tint = L.rectangle(
      [
        [-90, -180],
        [90, 180],
      ],
      { pane: paneName, stroke: false, fillColor: HEAT_GRADIENT[0.15], fillOpacity: 0.12, interactive: false },
    ).addTo(map);
    return () => {
      map.removeLayer(tint);
    };
  }, [map, metric, cells.length === 0]);

  useEffect(() => {
    if (!map || metric !== 'speed_value' || cells.length === 0 || !range) return undefined;
    const span = Math.max(range.max - range.min, 1e-6);
    const t = (value: number) => (range.max === range.min ? 1 : (value - range.min) / span);
    const sorted = cells;
    const canvas = L.DomUtil.create('canvas', 'leaflet-layer') as HTMLCanvasElement;
    canvas.style.pointerEvents = 'none';
    // own pane above the road lines (overlayPane, 400) and below the markers (600)
    const pane = map.getPane('heatmapSpeed') ?? map.createPane('heatmapSpeed');
    pane.style.zIndex = '450';
    pane.appendChild(canvas);
    let drawn: Array<{ x: number; y: number; r: number; cell: HeatmapCell }> = [];

    const redraw = () => {
      const size = map.getSize();
      const padX = size.x * CANVAS_PAD;
      const padY = size.y * CANVAS_PAD;
      const w = size.x + 2 * padX;
      const h = size.y + 2 * padY;
      const dpr = window.devicePixelRatio || 1;
      L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([-padX, -padY]));
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.globalAlpha = 0.7;
      drawn = [];
      sorted.forEach((cell) => {
        const p = map.latLngToContainerPoint([cell.lat, cell.lng]);
        const r = CELL_RADIUS_PX;
        const x = p.x + padX;
        const y = p.y + padY;
        if (x < -r || y < -r || x > w + r || y > h + r) return;
        ctx.fillStyle = rampColor(t(cell.value));
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
        drawn.push({ x: p.x, y: p.y, r, cell });
      });
      canvas.style.opacity = '1';
    };

    const tooltip = L.tooltip({ sticky: true, direction: 'top', offset: [0, -8] });
    const onMove = (e: L.LeafletMouseEvent) => {
      // top-most (last drawn) disc under the cursor
      let hit: (typeof drawn)[number] | undefined;
      for (let i = drawn.length - 1; i >= 0 && !hit; i -= 1) {
        const d = drawn[i];
        if ((d.x - e.containerPoint.x) ** 2 + (d.y - e.containerPoint.y) ** 2 <= d.r ** 2) hit = d;
      }
      if (!hit) {
        map.closeTooltip(tooltip);
        return;
      }
      tooltip
        .setLatLng(e.latlng)
        .setContent(
          l10n.getString('heatmap-tooltip-speed', { value: Math.round(hit.cell.value).toLocaleString() }),
        );
      map.openTooltip(tooltip);
    };
    const hide = () => {
      canvas.style.opacity = '0'; // the old frame would be at the wrong scale during the zoom animation
    };

    map.on('moveend zoomend resize', redraw);
    map.on('zoomstart', hide);
    map.on('mousemove', onMove);
    redraw();
    return () => {
      map.off('moveend zoomend resize', redraw);
      map.off('zoomstart', hide);
      map.off('mousemove', onMove);
      map.closeTooltip(tooltip);
      L.DomUtil.remove(canvas);
    };
  }, [map, cells, range, metric, l10n]);

  if (!enabled) return null;
  const ready = status === 'ready' && ds;
  return (
    <>
      {status === 'loading' && (
        <div className={`${classes.overlay} ${classes.loading}`} aria-label={l10n.getString('heatmap-loading')}>
          <CircularProgress size={48} />
        </div>
      )}
      {status === 'error' && (
        <div className={classes.banner}>
          <Typography variant="body2" color="error">
            {l10n.getString('heatmap-error', { error: error ?? '' })}
          </Typography>
          <Button size="small" onClick={request}>
            {l10n.getString('heatmap-retry')}
          </Button>
        </div>
      )}
      {ready && cells.length === 0 && (
        <div className={classes.banner}>
          <Typography variant="body2">{l10n.getString('heatmap-empty')}</Typography>
        </div>
      )}
      {ready && range && cells.length > 0 && (
        <div
          className={classes.legend}
          title={l10n.getString(scaleToVisibleData ? 'heatmap-scale-visible' : 'heatmap-scale-fixed')}
        >
          <Typography variant="caption" style={{ fontWeight: 600, fontSize: 10, lineHeight: 1.2 }}>
            {metric === 'speed_value'
              ? l10n.getString('heatmap-legend-speed')
              : l10n.getString('heatmap-legend-counter')}
          </Typography>
          <Typography variant="caption">{Math.round(range.max).toLocaleString()}</Typography>
          <div className={classes.legendBar} style={{ background: legendCss(metric) }} aria-hidden />
          <Typography variant="caption">
            {metric === 'counter' ? 0 : Math.round(range.min).toLocaleString()}
          </Typography>
        </div>
      )}
    </>
  );
};

export default React.memo(HeatmapLayer);
